import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2 } from "lucide-react";
import axios from "axios";
import { supabase } from "../lib/supabase"; // ajusta la ruta si tu cliente está en otro sitio

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const VideoPlayer = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [video, setVideo] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddAnnotation, setShowAddAnnotation] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState("");
  const [currentTime, setCurrentTime] = useState(0);

  // Axios instance con baseURL
  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE,
      timeout: 30000,
    });
  }, []);

  // Helper: obtiene token de sesión
  const getTokenOrRedirect = async () => {
    const { data, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;

    const token = data?.session?.access_token;
    if (!token) {
      navigate("/login");
      throw new Error("No hay sesión activa");
    }
    return token;
  };

  // Helper: construye URL reproducible
  const buildPlayableUrl = (maybeUrl) => {
    if (!maybeUrl) return "";
    if (maybeUrl.startsWith("http://") || maybeUrl.startsWith("https://")) return maybeUrl;
    if (maybeUrl.startsWith("/")) return `${BACKEND_URL}${maybeUrl}`;
    return `${BACKEND_URL}/${maybeUrl}`;
  };

  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      setLoading(true);
      setError("");

      try {
        const token = await getTokenOrRedirect();

        // 1) VIDEO
        const v = await api.get(`/videos/${videoId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!cancelled) setVideo(v.data);

        // 2) ANOTACIONES
        try {
          const a = await api.get(`/annotations/${videoId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!cancelled) setAnnotations(Array.isArray(a.data) ? a.data : []);
        } catch (annErr) {
          if (!cancelled) setAnnotations([]);
        }
      } catch (err) {
        console.error("Error loading video/player:", err);
        if (!cancelled) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail || err?.response?.data || err?.message;

          setError(
            status
              ? `Error al cargar el vídeo (${status}): ${
                  typeof detail === "string" ? detail : JSON.stringify(detail)
                }`
              : "Error al cargar el vídeo"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [videoId, api, navigate]);

  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handleAddAnnotation = async () => {
    if (!newAnnotation.trim()) return;

    try {
      const token = await getTokenOrRedirect();

      // ✅ IMPORTANTE: el backend espera Form(...) -> hay que enviar FormData
      const formData = new FormData();
      formData.append("video_id", videoId);
      formData.append("timestamp", String(currentTime));
      formData.append("note", newAnnotation);

      const response = await api.post(`/annotations`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const created = response.data;
      const next = [...annotations, created].sort(
        (a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)
      );
      setAnnotations(next);

      setNewAnnotation("");
      setShowAddAnnotation(false);
    } catch (err) {
      console.error("Error adding annotation:", err);
      setError("Error añadiendo anotación");
    }
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      const token = await getTokenOrRedirect();

      await api.delete(`/annotations/${annotationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAnnotations((prev) => prev.filter((a) => a.id !== annotationId));
    } catch (err) {
      console.error("Error deleting annotation:", err);
      setError("Error borrando anotación");
    }
  };

  const jumpToTime = (t) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
    videoRef.current.play?.();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-lg md:text-xl">Cargando vídeo...</div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <div className="p-6 md:p-10 flex flex-col items-center justify-center min-h-screen">
          <div className="text-white text-lg md:text-xl mb-4">
            {error || "Video no encontrado"}
          </div>
          <Link
            to="/dashboard"
            className="text-yellow-400 hover:text-yellow-300 text-sm md:text-base"
          >
            Volver al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const playableUrl = buildPlayableUrl(video.video_url);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-white/90 hover:text-white transition"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm md:text-base">Volver</span>
            </button>
          </div>

          <div className="text-white font-semibold text-base md:text-lg truncate max-w-[60%]">
            {video.title || "Video"}
          </div>

          <div />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video */}
        <div className="lg:col-span-2 bg-white/10 border border-white/20 rounded-2xl overflow-hidden">
          <div className="p-3 md:p-5">
            {/* ✅ REPRODUCCIÓN FIX:
                No forzamos type="video/webm" porque si el archivo real es mp4,
                Chrome puede intentar decodificarlo como webm y fallar.
                Dejamos que el navegador use el Content-Type real servido por Supabase.
            */}
            <video
              ref={videoRef}
              src={playableUrl || undefined}
              key={playableUrl || "video"}
              controls
              playsInline
              onTimeUpdate={handleTimeUpdate}
              className="w-full aspect-video rounded-xl bg-black"
              data-testid="video-element"
            >
              Tu navegador no soporta la reproducción de video.
            </video>

            <div className="mt-4 text-white/90">
              <div className="text-xl font-bold">{video.title}</div>
              {video.description ? (
                <div className="text-white/70 mt-1">{video.description}</div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Annotations */}
        <div className="bg-white/10 border border-white/20 rounded-2xl overflow-hidden">
          <div className="p-4 md:p-5 flex items-center justify-between">
            <div className="text-white text-lg font-bold">Anotaciones</div>
            <button
              onClick={() => {
                setError("");
                setShowAddAnnotation(true);
                setNewAnnotation("");
              }}
              className="bg-yellow-400 hover:bg-yellow-300 transition text-black font-bold rounded-xl p-2"
              title="Añadir anotación"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {error ? (
            <div className="mx-4 mb-4 bg-red-500/80 text-white text-sm rounded-xl px-3 py-2">
              {error}
            </div>
          ) : null}

          {showAddAnnotation ? (
            <div className="mx-4 mb-4 bg-white/10 border border-white/20 rounded-xl p-4">
              <div className="text-white/90 text-sm mb-2">
                Tiempo: {new Date(currentTime * 1000).toISOString().slice(14, 19)}
              </div>

              <textarea
                value={newAnnotation}
                onChange={(e) => setNewAnnotation(e.target.value)}
                className="w-full bg-white text-black rounded-lg p-3 outline-none"
                placeholder="Escribe tu anotación..."
                rows={4}
              />

              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleAddAnnotation}
                  className="flex-1 bg-green-600 hover:bg-green-500 transition text-white font-bold py-2 rounded-lg"
                >
                  Guardar
                </button>
                <button
                  onClick={() => {
                    setShowAddAnnotation(false);
                    setNewAnnotation("");
                  }}
                  className="bg-red-600 hover:bg-red-500 transition text-white font-bold px-4 rounded-lg"
                  title="Cancelar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : null}

          <div className="px-4 pb-5">
            {annotations.length === 0 ? (
              <div className="text-white/70 text-sm py-6 text-center">
                No hay anotaciones aún. ¡Añade la primera!
              </div>
            ) : (
              <div className="space-y-2">
                {annotations.map((a) => {
                  const t = Number(a.timestamp || 0);
                  const tLabel = new Date(t * 1000).toISOString().slice(14, 19);

                  return (
                    <div
                      key={a.id}
                      className="bg-white/10 border border-white/20 rounded-xl p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => jumpToTime(t)}
                          className="text-yellow-300 hover:text-yellow-200 transition text-sm font-bold"
                          title="Ir a este momento"
                        >
                          {tLabel}
                        </button>

                        <button
                          onClick={() => handleDeleteAnnotation(a.id)}
                          className="text-white/60 hover:text-white transition"
                          title="Borrar anotación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="text-white/90 text-sm mt-2 whitespace-pre-wrap">
                        {a.note}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
